<template>
    <div class="h-full">
        <div class="flex flex-wrap">
            <div class="w-1/12"></div>

            <div class="w-full lg:w-10/12 mb-2 text-center">
                <h1 class="text-3xl capitalize">
                    Registrar inventario para
                    {{ selectedMerchant.merchantType | merchantInfo("type") }}
                    <strong class="text-theme-primary">
                        {{ selectedMerchant.name }}
                    </strong>
                </h1>
            </div>
            <div class="w-full lg:w-1/12 flex justify-center">
                <custom-tooltip border>
                    <app-button
                        icon
                        border
                        :active="enableHelp"
                        @click="enableHelp = !enableHelp"
                    >
                        ℹ️
                    </app-button>
                    <template #tooltip>
                        <span>
                            ¿ Necesitas ayuda para registrar inventario ?
                        </span>
                    </template>
                </custom-tooltip>
            </div>
        </div>

        <process-guide
            v-model="enableHelp"
            title="¿Cómo registrar inventario?"
            intro="En esta sección puedes registrar inventario para tu tienda."
            :steps="[
                'Busca los productos que deseas agregar: usa el buscador de la parte inferior o escanea el código de barras del producto.',
                'Ingresa las cantidades que deseas agregar, su costo correspondiente y, si es necesario, el precio de venta.',
                'Haz clic en el botón Registrar inventario, que se habilita una vez ingresados los datos.'
            ]"
        />

        <div v-if="!isOnboarding" class="flex flex-wrap">
            <div
                v-if="$ability.hasPermission('cashOutput:Create')"
                class="my-2 w-full md:w-4/12"
            >
                <custom-checkbox v-model="needCashOutput" warn label-before>
                    <span> Crear tambien como un gasto</span>
                </custom-checkbox>
            </div>
        </div>
        <div v-else class="text-justify">
            <span>
                Es momento de agregar inventario a tu nueva tienda. Ingresa en
                el buscador de la parte inferior la palabra
                <strong> "cuaderno" </strong>. Selecciona una de las opciones
                que se deesplegará. Ingresa el valor al que quieres vender el
                producto y lo que te ha costado, nosotros te recomendaremos un
                precio. Finalmete da click en el botón "Registrar inventario".
            </span>
        </div>

        <div class="flex flex-col mt-3 justify-center items-center">
            <span class="text-center font-bold">
                Selecciona las referencias que vas a ingresar al inventario
            </span>
            <div class="mt-3 w-full">
                <inventories-search-scanner
                    :enabled-scanner="enabledScanner"
                    :auto-focus="isOnboarding"
                    @input="validatePushReferences"
                />
            </div>
        </div>

        <div class="flex flex-wrap z-0 mt-3 min-h-32">
            <h4 v-show="references.length" class="mx-2 mt-3">Variantes</h4>
            <reference-inventory-items
                v-for="(reference, index) in references"
                :key="index"
                class="md:w-11/12 w-full mx-auto"
                required-price
                :reference="reference"
                @update="changeReference"
                @remove="removeReference"
                @focus="onFocusSomeInput"
                @blur="onBlurSomeInput"
            />
        </div>

        <div v-if="references.length > 0" class="flex flex-wrap z-0 mt-4">
            <div class="w-10/12 mx-auto flex justify-end py-3">
                <span class="text-lg">
                    Valor referencias {{ total | money }}
                </span>
            </div>
        </div>

        <div v-if="needCashOutput" class="flex flex-wrap">
            <div class="w-full flex flex-col lg:flex-row justify-between">
                <payment-methods-checker v-model="sourcePay" mode="output">
                    <template #title>
                        <span class="font-bold"> Método de pago </span>
                    </template>
                </payment-methods-checker>
            </div>

            <div class="w-full md:w-1/2 mt-3 px-2">
                <div class="flex items-center justify-between">
                    <strong>Concepto</strong>
                    <custom-tooltip border>
                        <app-button icon circle border> ℹ </app-button>
                        <template #tooltip> Resume lo que compraste</template>
                    </custom-tooltip>
                </div>
                <custom-input
                    v-model="concept"
                    type="text"
                    name="concept"
                    placeholder="Servicio tucatalogo.co"
                    class="w-full"
                    :error="
                        !isValidConcept & introValidators.concept
                            ? 'El cocepto debe tener mínimo un caracter'
                            : ''
                    "
                    @focus="onFocusSomeInput"
                    @blur="onBlurConcept"
                />
            </div>
            <div class="w-full md:w-1/2 mt-3 px-2">
                <div class="flex items-center justify-between">
                    <strong>Proveedor</strong>
                    <custom-tooltip border>
                        <app-button icon circle border> ℹ </app-button>
                        <template #tooltip> Nombre de tu proveedor </template>
                    </custom-tooltip>
                </div>
                <custom-input
                    v-model="provider"
                    type="text"
                    placeholder=""
                    name="provider"
                    class="w-full"
                    @focus="onFocusSomeInput"
                    @blur="onBlurProvider"
                />
            </div>
            <div v-if="$enabledEcommerceOptions" class="w-full mt-3">
                <private-file-uploader v-model="receiptFile" auto-loader />
            </div>
        </div>

        <div
            v-if="isOnboarding"
            class="flex justify-center my-3 link-secondary"
        >
            <router-link to="/app"> Omitir </router-link>
        </div>
        <div class="flex justify-center my-3">
            <app-button
                class="mt-3 mb-5 w-3/4 mx-auto"
                border
                :disabled="!references.length"
                gradient
                @click="createInventoryComponent"
            >
                <span class="text-2xl"> Registrar inventario </span>
            </app-button>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import { titleRegex } from "@/common/lib/regex";

import InventoriesSearchScanner from "@/components/inventories/InventoriesSearchScanner.vue";
import PaymentMethodsChecker from "@/components/orders/PaymentMethodsChecker.vue";
import ReferenceInventoryItems from "@/components/references/ReferenceInventoryItems.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import PrivateFileUploader from "@/components/utils/PrivateFileUploader.vue";
import ProcessGuide from "@/components/utils/ProcessGuide.vue";

export default {
    name: "NewInventory",
    components: {
        PrivateFileUploader,
        ReferenceInventoryItems,
        InventoriesSearchScanner,
        PaymentMethodsChecker,
        CustomInput,
        ProcessGuide
    },
    data: () => ({
        provider: "",
        concept: "",
        receiptFile: null,
        needCashOutput: false,
        enabledScanner: false,
        isOnboarding: false,
        total: 0,
        moneyBack: 0,
        sourcePay: "cash",
        enableHelp: false,
        references: [],
        introValidators: { provider: false, concept: false }
    }),
    computed: {
        ...mapState("commons", ["selectedMerchant"]),
        ...mapGetters("control", ["backgroundColor"]),
        isValidProvider() {
            return titleRegex.test(this.provider);
        },
        isValidConcept() {
            return titleRegex.test(this.concept);
        }
    },
    created() {
        this.isOnboarding = this.$route.path.includes("onboarding");
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (
            !(await verifyPagePermission(
                "inventory:Create",
                "/app/inventories",
                true
            ))
        )
            return;

        this.enabledScanner = !this.isOnboarding;
    },
    methods: {
        ...mapActions("inventories", ["createManyInventories"]),
        async createInventoryComponent() {
            if (!this.validateData()) {
                return;
            }

            this.$loading.show();
            try {
                const result = await this.createManyInventories({
                    merchantId: this.selectedMerchant.merchantId,
                    merchantType: this.selectedMerchant.merchantType,
                    references: this.references.map((item) => ({
                        referenceId: item.id,
                        quantity: parseInt(item.quantity),
                        costNew: item.costNew,
                        priceNew: item.priceNew
                    })),
                    ...(this.needCashOutput
                        ? {
                              cashOutput: {
                                  concept: this.concept,
                                  provider: this.provider,
                                  sourcePay: this.sourcePay
                              }
                          }
                        : null)
                });

                this.$analytics.trackEvent("inventory_registered", {
                    items_count: this.references.length,
                    inventories_recorded: result.inventoriesRecorded,
                    inventories_updated: result.inventoriesUpdated,
                    with_cash_output: this.needCashOutput
                });
                this.$loading.hide();
                this.$sound.play();
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Operación exitosa",
                    text: `🟢 Productos nuevos en inventario: ${result.inventoriesRecorded}.
                    🟢 Actualizaciones de stock ${result.inventoriesUpdated}.`,
                    icon: "success",
                    showCloseButton: true
                });
                this.resetItem();
                if (this.isOnboarding) {
                    return this.$router.push("/onboarding/order");
                }
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        calculateTotals() {
            this.total = this.references.reduce(
                (a, b) => a + b.costNew * b.quantity,
                0
            );
        },
        validatePushReferences(reference) {
            const index = this.references.findIndex(
                (item) => item.id === reference.id
            );
            if (index === -1) {
                this.references.unshift({
                    ...reference,
                    priceNew: reference.priceOffer,
                    costNew: reference.cost,
                    quantity: 1
                });
            } else {
                let references = [...this.references];
                references[index] = {
                    ...references[index],
                    quantity: references[index].quantity + 1
                };
                this.references = [...references];
            }
            this.calculateTotals();
        },
        changeReference({ costNew, id, priceNew, quantity }) {
            const index = this.references.findIndex((item) => item.id == id);
            let references = [...this.references];
            references[index] = {
                ...references[index],
                quantity,
                costNew,
                priceNew
            };
            this.references = [...references];
            this.calculateTotals();
        },
        removeReference({ id }) {
            const index = this.references.findIndex((item) => item.id == id);
            let references = [...this.references];
            references.splice(index, 1);
            this.references = [...references];
            this.calculateTotals();
        },
        onBlurConcept() {
            this.onBlurSomeInput();
        },
        onBlurProvider() {
            this.onBlurSomeInput();
        },
        onBlurSomeInput() {
            this.enabledScanner = true;
        },
        onFocusSomeInput() {
            this.enabledScanner = false;
        },
        resetItem() {
            this.references = [];
            this.enabledScanner = !this.$isPhone;
            this.provider = "";
            this.concept = "";
            this.needCashOutput = false;
            this.needCashOutput = false;
            this.total = 0;
            this.introValidators = {
                provider: false,
                concept: false
            };
        },
        validateData() {
            const toComplete = [];
            if (this.needCashOutput) {
                this.introValidators = {
                    provider: true,
                    concept: true
                };
                if (!this.isValidProvider) {
                    toComplete.push("Proveedor");
                }
                if (!this.isValidConcept) {
                    toComplete.push("Concepto");
                }
            }

            if (!this.references.length) {
                toComplete.push("Variantes");
            }
            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Completa la información",
                    text: `Revisa por favor que todo este completo. Valida los siguientes campos: ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }
            return true;
        }
    }
};
</script>
